import { Injectable } from '@angular/core';
import { Router, UrlTree } from '@angular/router';
import { Observable, firstValueFrom } from 'rxjs';
import { environment } from '../../../environments/environment';
import { Capacitor } from '@capacitor/core';
import { Browser } from '@capacitor/browser';
import { Directory, Filesystem } from '@capacitor/filesystem';
import { Share } from '@capacitor/share';

/**
 * Browser-only I/O that is a no-op inside a Capacitor WebView, routed to the
 * native equivalent. Keeps the public site and the app on the same features:
 *
 *  - `window.open('/some/route')`  -> no system browser can load capacitor://
 *  - anchor `download` of a blob   -> silently does nothing on iOS
 *  - `window.location.href = ...`  -> navigates the shell off the local bundle
 */
@Injectable({ providedIn: 'root' })
export class NativeIoService {
  constructor(private readonly router: Router) {}

  get isNative(): boolean {
    return Capacitor.isNativePlatform();
  }

  /** In-app route the site opens in a new tab. Native has no tabs: navigate in place. */
  openInternalRoute(commands: any[]): void {
    if (this.isNative) {
      this.router.navigate(commands);
      return;
    }
    const tree: UrlTree = this.router.createUrlTree(commands);
    window.open(this.router.serializeUrl(tree), '_blank');
  }

  /** Leave the app for a gateway/website URL without losing the shell. */
  async openExternalUrl(url: string): Promise<void> {
    if (this.isNative) {
      await Browser.open({ url });
      return;
    }
    window.location.href = url;
  }

  /** Runs `handler` once the in-app browser is dismissed (native only). */
  async onExternalUrlClosed(handler: () => void): Promise<void> {
    if (!this.isNative) {
      return;
    }
    const sub = await Browser.addListener('browserFinished', () => {
      sub.remove();
      handler();
    });
  }

  /**
   * Save a file served by an authenticated API endpoint.
   *
   * Web subscribes to the HttpClient call and downloads the blob. Native CANNOT: CapacitorHttp
   * patches XHR and fetch, and its patch never forwards `responseType`, so the native layer
   * decodes every non-JSON body as UTF-8 text — a PDF comes back mangled. Native therefore lets
   * the Filesystem plugin do the HTTP itself (binary-safe) and opens the share sheet on the file.
   */
  async saveDownload(fileName: string, apiUrl: string, blob$: Observable<Blob>): Promise<void> {
    if (!this.isNative) {
      const blob = await firstValueFrom(blob$);
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = fileName;
      link.click();
      window.URL.revokeObjectURL(url);
      return;
    }

    await Filesystem.downloadFile({
      url: apiUrl,
      headers: this.apiHeaders(),
      path: fileName,
      directory: Directory.Cache,
    });
    const { uri } = await Filesystem.getUri({ path: fileName, directory: Directory.Cache });
    await Share.share({ title: fileName, url: uri });
  }

  /** Same headers ApiKeyInterceptor adds — the Filesystem download bypasses HttpClient. */
  private apiHeaders(): Record<string, string> {
    const headers: Record<string, string> = { 'X-API-KEY': environment.apiKey };
    const raw = window.localStorage.getItem('appToken');
    if (raw) {
      let token = raw;
      try {
        token = JSON.parse(raw); // LocalStorageService stores values JSON-stringified.
      } catch {
        /* stored unquoted */
      }
      headers['Authorization'] = `Bearer ${token}`;
    }
    return headers;
  }
}
